import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getAssessmentNoteRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";

interface NewbornAssessmentNoteRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  isViewOnly: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
}

export default function NewbornAssessmentNoteRow({
  time,
  observation,
  showNumbersOnly,
  isViewOnly,
  showPreviousVersions = true,
  onRefresh,
}: NewbornAssessmentNoteRowProps) {
  const { notes, hasNotes } = useMemo(
    () =>
      observation
        ? getAssessmentNoteRowValues(observation)
        : {
            notes: undefined,
            hasNotes: false,
          },
    [observation],
  );

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  if (!hasNotes || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <PostpartumTimeCell
          time={time}
          rowSpan={1}
          onRefresh={onRefresh}
          isViewOnly={isViewOnly}
          observation={observation}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        <TextWrapper size="sm">Assessment Note: {notes}</TextWrapper>
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={NewbornAssessmentNoteRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
